import * as Sentry from '@sentry/react'
import dayjs from 'dayjs'

import { CommonContentRepository, LoadPreSignUrlOutput, PublishAssetOutput } from 'src/repositories'
import { CommonContentAssetTranslator } from 'src/translators'
import {
  CommonContentAssetEntity,
  CommonContentAssetFormEntity,
  CommonContentAssetRequest,
} from 'src/types'
import {
  useCreateCommonContentAssetMutation,
  useUpdateCommonContentAssetMutation,
  useDeleteCommonContentAssetMutation,
  useReorderCommonContentAssetsMutation,
} from 'src/types/generated'

type CreateCommonContentAssetInput = {
  commonContentPageId: number
  pageOrder: number
}

type UpdateCommonContentAssetInput = {
  commonContentPageId: number
  assetId: number
  formEntity: CommonContentAssetFormEntity
}

type DeleteCommonContentAssetInput = {
  commonContentPageId: number
  assetId: number
}

type UploadAssetFileInput = {
  asset: CommonContentAssetEntity
  file: File
}

type ReorderCommonContentAssetsInput = {
  commonContentPageId: number
  updatedCommonContentAssetIds: number[]
}

export const useCommonContentAsset = () => {
  const [createCommonContentAssetMutation] = useCreateCommonContentAssetMutation()
  const [updateCommonContentAssetMutation] = useUpdateCommonContentAssetMutation()
  const [deleteCommonContentAssetMutation] = useDeleteCommonContentAssetMutation()
  const [reorderCommonContentAssetsMutation] = useReorderCommonContentAssetsMutation()

  const createCommonContentAsset = async (input: CreateCommonContentAssetInput) => {
    const { commonContentPageId, pageOrder } = input

    const parameters: CommonContentAssetRequest = {
      meta: {
        uploadedAt: dayjs().valueOf(),
      },
      priority: pageOrder,
    }
    await createCommonContentAssetMutation({
      variables: { commonPageId: commonContentPageId, params: parameters },
    })
  }

  const updateCommonContentAsset = async (input: UpdateCommonContentAssetInput) => {
    const { commonContentPageId, assetId, formEntity } = input

    const parameters: CommonContentAssetRequest = {
      name: formEntity.name,
      meta: {
        fileName: formEntity.name,
        uploadedAt: dayjs().valueOf(),
      },
      externalLink: formEntity.url,
      priority: formEntity.priority,
    }

    await updateCommonContentAssetMutation({
      variables: { commonPageId: commonContentPageId, commonAssetId: assetId, params: parameters },
    })
  }

  const deleteContentToAsset = async (asset: CommonContentAssetEntity) => {
    const parameters: CommonContentAssetRequest = {
      name: '',
      meta: {
        fileName: '',
        uploadedAt: dayjs().valueOf(),
      },
      externalLink: '',
      priority: asset.priority,
      fileSize: 0,
    }

    await updateCommonContentAssetMutation({
      variables: { commonPageId: asset.pageId, commonAssetId: asset.id, params: parameters },
    })
  }

  const deleteCommonContentAsset = async (input: DeleteCommonContentAssetInput) => {
    const { commonContentPageId, assetId } = input

    await deleteCommonContentAssetMutation({
      variables: { commonPageId: commonContentPageId, commonAssetId: assetId },
    })
  }

  const uploadAssetFile = async (input: UploadAssetFileInput) => {
    const { asset, file } = input

    // S3にアップロードするためのPreSignUrlを取得
    let loadPreSignUrlResponse: LoadPreSignUrlOutput
    try {
      loadPreSignUrlResponse = await CommonContentRepository.loadPreSignUrl({
        assetId: asset.id,
        file,
      })
    } catch (error) {
      Sentry.captureMessage(`【assetId: ${asset.id}】 S3のPreSignUrl取得時に失敗`)
      throw error
    }

    // S3にアップロード
    try {
      await CommonContentRepository.uploadToS3({ preSignUrl: loadPreSignUrlResponse.url, file })
    } catch (error) {
      Sentry.captureMessage(`【assetId: ${asset.id}】 S3のアップロードに失敗`)
      throw error
    }

    // S3にアップロードしたファイルを公開
    let publishAssetResponse: PublishAssetOutput
    try {
      publishAssetResponse = await CommonContentRepository.publishAsset(
        loadPreSignUrlResponse.dataUrl
      )
    } catch (error) {
      Sentry.captureMessage(`【assetId: ${asset.id}】 S3アップロード済みAssetの公開に失敗`)
      throw error
    }

    // ファイル名を名前に設定する場合は拡張子を削除する
    const fileName = file.name.replace(/\.[^./]+$/, '')

    // アップロード&公開した情報を元に、CommonContentAssetを更新
    const parameters: CommonContentAssetRequest = {
      name: asset.name && asset.name !== '' ? asset.name : fileName,
      meta: {
        fileName: file.name,
        uploadedAt: dayjs().valueOf(),
      },
      priority: asset.priority,
      fileSize: publishAssetResponse.fileSize,
    }

    await updateCommonContentAssetMutation({
      variables: { commonPageId: asset.pageId, commonAssetId: asset.id, params: parameters },
    })
  }

  const reorderCommonContentAssets = async (input: ReorderCommonContentAssetsInput) => {
    const { commonContentPageId, updatedCommonContentAssetIds } = input
    await reorderCommonContentAssetsMutation({
      variables: {
        commonPageId: commonContentPageId,
        params: { commonAssetsPriorities: updatedCommonContentAssetIds },
      },
    })
  }

  const toCommonContentAssetFormEntity = (
    entity: CommonContentAssetEntity
  ): CommonContentAssetFormEntity => {
    return CommonContentAssetTranslator.toFormEntity(entity)
  }

  return {
    createCommonContentAsset,
    updateCommonContentAsset,
    deleteContentToAsset,
    deleteCommonContentAsset,
    uploadAssetFile,
    reorderCommonContentAssets,
    toCommonContentAssetFormEntity,
  }
}
